'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@ahafood/ui/components/form';
import { Input } from '@ahafood/ui/components/input';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { Separator } from '@ahafood/ui/components/separator';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { useDebouncedValue } from '@ahafood/ui/hooks/use-debounced-value';
import { EmptySearch } from '@ahamove/icons/empty';
import {
  CurrentLocation,
  Address as MapAddress,
  Path,
} from '@ahamove/icons/map';
import { assetPrefix, type Address } from '@ahm/api-wrappers-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next/client';
import { Loader2, Search, X, XIcon } from 'lucide-react';
import React, { Fragment, memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useWebstoreSession } from '../auth/webstore-session-provider';
import LocationLoadingScreen from './loading-location';
import { getGoogleAddressFn } from '@/api/ahafood/get-address-detail-fn';
import { useAddAddress } from '@/api/use-add-new-address';
import { useAutocomplete } from '@/api/use-autocomplete';
import { getAuthToken } from '@/lib/auth/get-token';
import { useAiFoodOrderStore, useLocationStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';

const FormSchema = z.object({
  keyword: z.string().optional(),
});

const AddressSuggestion = memo(
  ({
    suggestion,
    onSelect,
  }: {
    suggestion: Address;
    onSelect: (address: Address) => void;
  }) => (
    <Card
      className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full cursor-pointer"
      onClick={() => onSelect(suggestion)}
    >
      <MapAddress className="size-6 flex-none" />
      <CardHeader className="p-0 w-full max-w-[calc(100%-2.5rem)]">
        <CardTitle className="truncate">
          {suggestion.address.split(',')[0]}
        </CardTitle>
        <CardDescription className="truncate">
          {suggestion.address.split(',').slice(1).join(',')}
        </CardDescription>
      </CardHeader>
    </Card>
  )
);

AddressSuggestion.displayName = 'AddressSuggestion';

const SavedAddressItem = memo(
  ({
    userLocation,
    onSelect,
  }: {
    userLocation: any;
    onSelect: (address: Address) => void;
  }) => {
    const handleSelect = () => {
      const address: Address = {
        lat: userLocation.lat,
        lng: userLocation.lng,
        place_id: userLocation.place_id,
        address: userLocation.address,
      };
      onSelect(address);
    };

    return (
      <Card
        className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full cursor-pointer"
        onClick={handleSelect}
      >
        <MapAddress className="size-6 flex-none text-primary-50" />
        <CardHeader className="p-0 w-full max-w-[calc(100%-2.5rem)]">
          <CardTitle className="truncate">
            {userLocation.address.split(',')[0]}
          </CardTitle>
          <CardDescription className="line-clamp-2">
            {userLocation.address.split(',').slice(1).join(',')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
);

SavedAddressItem.displayName = 'SavedAddressItem';

const AddressSuggestionSkeleton = () => (
  <Card className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full">
    <Skeleton className="size-6 flex-none rounded-md" />
    <div className="p-0 w-full max-w-[calc(100%-2.5rem)] flex flex-col gap-2">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </Card>
);

// const SavedAddressSkeleton = () => (
//   <div className="mt-4 rounded-md">
//     <div className="mb-3 px-0">
//       <Skeleton className="h-4 w-24" />
//     </div>
//     <div className="space-y-0">
//       {[...Array(3)].map((_, index) => (
//         <Fragment key={index}>
//           {index > 0 && <Separator className="ml-14 w-[calc(100%-3.5rem)]" />}
//           <Card className="flex flex-row px-0 py-3 flex-nowrap gap-4 items-start border-none rounded-none shadow-none w-full">
//             <Skeleton className="size-6 flex-none rounded-md" />
//             <div className="p-0 w-full max-w-[calc(100%-2.5rem)] flex flex-col gap-2">
//               <Skeleton className="h-5 w-3/4" />
//               <Skeleton className="h-4 w-1/2" />
//             </div>
//           </Card>
//         </Fragment>
//       ))}
//     </div>
//   </div>
// );

export default function OrderLocationForm({ handleClose }) {
  const queryClient = useQueryClient();
  const { closeLocationSheet, mode, storeData } = useWebstoreSession();

  const address = useCartStore((state) => state.address);
  const [addressError, setAddressError] = useState<string | null>(null);
  const detectLocation = useLocationStore.use.detectLocation();
  const loadingLocation = useLocationStore.use.loading();
  const setAddressWS = useAiFoodOrderStore.use.setAddress();
  const setAddress = useCartStore((state) => state.setAddress) as (
    address: Address | null
  ) => void;

  // const session.access_token = getCookie('token') as string;

  // console.log('systemToken', systemToken);
  const generatePlaceId = (lat: number, lng: number): string => {
    return `${lat}_${lng}`;
  };

  // Use the same hooks as StoreCityIdDialog when mode === 'webstore'
  const systemToken = getAuthToken();
  const addAddressMutation = useAddAddress(systemToken, {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['user-locations'],
      });
    },
    onError: () => {
      toast.error('Có lỗi khi lưu địa chỉ, vui lòng thử lại sau');
    },
  });

  // Fetch saved addresses for 'webstore' mode
  const [userLocationsData, setUserLocationsData] = useState<any>(null);
  useEffect(() => {
    if (mode === 'webstore' && !!systemToken) {
      void queryClient
        .fetchQuery({
          queryKey: ['user-locations'],
        })
        .then((data) => {
          setUserLocationsData(data);
        });
    }
  }, [mode, systemToken, queryClient]);

  const [loadingAddressDetail, setLoadingAddressDetail] = useState(false);
  const [preventAutocomplete, setPreventAutocomplete] = useState(false);
  const [selectedCurrentLocation, setSelectedCurrentLocation] =
    useState<Address | null>(null);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      keyword: address?.address ?? '',
    },
    mode: 'onChange',
  });

  const keywordValue = form.watch('keyword') ?? '';
  const [debouncedKeyword] = useDebouncedValue(keywordValue, 500);

  const { data: addressOptions, isLoading: loading } = useAutocomplete(
    { input: debouncedKeyword },
    {
      queryKey: ['autocomplete', { input: debouncedKeyword }],
      enabled: Boolean(
        debouncedKeyword && debouncedKeyword.length >= 2 && !preventAutocomplete
      ),
      refetchOnWindowFocus: false,
    }
  );

  // Auto-set first saved address if no address is currently selected (only on initial load)
  const [hasInitialized, setHasInitialized] = useState(false);
  const [userClearedInput, setUserClearedInput] = useState(false);
  const [showLocationLoading, setShowLocationLoading] = useState(false);

  useEffect(() => {
    if (address?.address && !keywordValue && !userClearedInput) {
      form.setValue('keyword', address.address);
    }
  }, [address, form, keywordValue, userClearedInput]);

  const [pendingAddress, setPendingAddress] = useState<Address | null>(null);

  useEffect(() => {
    if (
      !hasInitialized &&
      !address?.lat &&
      !address?.lng &&
      form.getValues('keyword') === '' &&
      userLocationsData?.address_histories &&
      userLocationsData.address_histories.length > 0
    ) {
      const firstSavedAddress = userLocationsData.address_histories[0];
      const addressToSet: Address = {
        lat: firstSavedAddress.lat,
        lng: firstSavedAddress.lng,
        place_id: firstSavedAddress.place_id,
        address: firstSavedAddress.address,
      };

      setPendingAddress(addressToSet);
      setShowLocationLoading(true);

      setTimeout(() => {
        setAddress(addressToSet);
        form.setValue('keyword', firstSavedAddress.address);
        setHasInitialized(true);
        setShowLocationLoading(false);
        setPendingAddress(null);
      }, 1500);
    }
  }, [
    address,
    userLocationsData,
    setAddress,
    form,
    hasInitialized,
    setShowLocationLoading,
  ]);

  useEffect(() => {
    if (
      debouncedKeyword &&
      debouncedKeyword.length >= 2 &&
      !preventAutocomplete
    ) {
      void queryClient.invalidateQueries({
        queryKey: ['autocomplete', { input: debouncedKeyword }],
      });
    }
  }, [debouncedKeyword, preventAutocomplete, queryClient]);

  const handleAddressSelect = useCallback(
    (selectedAddress: Address) => {
      setPreventAutocomplete(true);
      setUserClearedInput(false);

      const addAddressToServer = (addressToAdd: Address) => {
        if (
          addressToAdd?.address &&
          !!systemToken &&
          typeof addressToAdd.lat === 'number' &&
          typeof addressToAdd.lng === 'number' &&
          !addAddressMutation.isPending
        ) {
          const placeId =
            addressToAdd.place_id ??
            generatePlaceId(addressToAdd.lat, addressToAdd.lng);
          addAddressMutation.mutate({
            address: addressToAdd.address,
            place_id: placeId,
            lat: addressToAdd.lat,
            lng: addressToAdd.lng,
          });
        }
      };

      if (
        (!selectedAddress.lat || !selectedAddress.lng) &&
        selectedAddress.place_id
      ) {
        setLoadingAddressDetail(true);
        form.setValue('keyword', selectedAddress.address || '');

        getGoogleAddressFn({
          place_id: selectedAddress.place_id,
          sessiontoken: selectedAddress.sessiontoken,
        })
          .then((res: Address | null) => {
            if (res) {
              setAddress(res);
              setAddressWS(res);
              setAddressError(null);
              form.setValue('keyword', res?.address || '');

              if (res.lat && res.lng && res.address && mode === 'webstore') {
                addAddressToServer({
                  ...res,
                  place_id: res.place_id ?? generatePlaceId(res.lat, res.lng),
                });
              }

              closeLocationSheet();
            } else {
              setAddress(null);
              setAddressError(
                'Không tìm được vị trí tương ứng với địa chỉ bạn chọn.'
              );
            }
          })
          .catch(() => {
            toast.error(
              'Đã có lỗi khi chọn địa chỉ của bạn, vui lòng thử lại sau'
            );
            setAddress(null);
          })
          .finally(() => {
            setLoadingAddressDetail(false);
          });
      } else {
        form.setValue('keyword', selectedAddress?.address || '');
        setAddress(selectedAddress);
        setAddressWS(selectedAddress);

        if (
          selectedAddress.lat &&
          selectedAddress.lng &&
          selectedAddress.address &&
          mode === 'webstore'
        ) {
          addAddressToServer({
            ...selectedAddress,
            place_id:
              selectedAddress.place_id ??
              generatePlaceId(selectedAddress.lat, selectedAddress.lng),
          });
        }

        closeLocationSheet();
      }
    },
    [
      form,
      setAddress,
      setAddressWS,
      closeLocationSheet,
      addAddressMutation,
      systemToken,
      mode,
    ]
  );

  const handleUseCurrentLocation = useCallback(() => {
    setPreventAutocomplete(true);

    let isCompleted = false;

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        if (!isCompleted) {
          useLocationStore.setState({
            loading: false,
            error: new Error('Timeout'),
          });
          reject(new Error('Timeout: Không thể xác định vị trí trong 5 giây'));
        }
      }, 5000);
    });

    Promise.race([detectLocation(), timeoutPromise])
      .then((response) => {
        isCompleted = true;
        setSelectedCurrentLocation(response);
        form.setValue('keyword', response.address || '');
      })
      .catch((error) => {
        isCompleted = true;
        console.error(error);
        setPreventAutocomplete(false);
        setSelectedCurrentLocation(null);
        useLocationStore.setState({ loading: false });
        toast.error('Vui lòng cho phép bật vị trí trên trình duyệt này');
      });
  }, [detectLocation, form]);

  const handleConfirmLocation = useCallback(() => {
    if (selectedCurrentLocation) {
      setAddress(selectedCurrentLocation);
      setAddressWS(selectedCurrentLocation);
      setAddressError(null);

      if (
        selectedCurrentLocation.lat &&
        selectedCurrentLocation.lng &&
        selectedCurrentLocation.place_id &&
        selectedCurrentLocation.address &&
        mode === 'webstore' &&
        !addAddressMutation.isPending
      ) {
        addAddressMutation.mutate({
          address: selectedCurrentLocation.address,
          place_id: selectedCurrentLocation.place_id,
          lat: selectedCurrentLocation.lat,
          lng: selectedCurrentLocation.lng,
        });
      }

      closeLocationSheet();
    } else {
      setAddressError(
        'Không thể xác nhận địa chỉ. Vui lòng chọn hoặc nhập lại.'
      );
    }
  }, [
    selectedCurrentLocation,
    setAddress,
    setAddressWS,
    closeLocationSheet,
    addAddressMutation,
    mode,
  ]);

  const handleClearInput = useCallback(() => {
    form.setValue('keyword', '');
    setPreventAutocomplete(false);
    setAddress(null);
    setAddressError(null);
    setSelectedCurrentLocation(null);
    setLoadingAddressDetail(false);
    setHasInitialized(true);
    setUserClearedInput(true);
  }, [form, setAddress]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setValue('keyword', e.target.value);
      setPreventAutocomplete(false);
      setSelectedCurrentLocation(null);
      setUserClearedInput(false);
    },
    [form]
  );

  const handleFormChange = useCallback(() => {
    setPreventAutocomplete(false);
  }, []);

  const totalLoading = loading || loadingLocation || loadingAddressDetail;

  const shouldShowSuggestions = Boolean(
    !loadingAddressDetail &&
      !preventAutocomplete &&
      !totalLoading &&
      debouncedKeyword &&
      addressOptions &&
      addressOptions.length > 0
  );

  const shouldShowEmptyResults = Boolean(
    !totalLoading &&
      debouncedKeyword &&
      addressOptions &&
      addressOptions.length === 0
  );

  const shouldShowSavedAddresses = Boolean(
    !shouldShowSuggestions &&
      !shouldShowEmptyResults &&
      !totalLoading &&
      mode === 'webstore' &&
      userLocationsData?.address_histories &&
      userLocationsData.address_histories.length > 0
  );

  return (
    <ScrollArea className="[&>div>div]:gap-2 [&>div>div]:!flex [&>div>div]:flex-col [&>div>div]:w-full [&>div>div]:pb-28 h-[calc(100svh)] md:h-full md:max-h-[90vh] md:[&>div>div]:pb-4">
      <Card className="overflow-hidden relative border-none rounded-none shadow-none">
        <Avatar className="h-32 w-full rounded-none flex-none">
          <Button
            size="icon"
            variant="secondary"
            className="[&_svg]:size-6 flex-none rounded-full absolute top-4 right-4 z-10"
            onClick={handleClose}
            type="button"
          >
            <XIcon />
          </Button>
          <AvatarImage
            src={
              storeData?.image_url ??
              `${assetPrefix}/static/images//empty-store.webp`
            }
            alt={'Cửa hàng'}
            className="object-top object-cover"
          />
          <AvatarFallback className="rounded-none">
            {storeData?.name ?? 'Cửa hàng'}
          </AvatarFallback>
        </Avatar>
        <CardHeader className="p-4 w-full">
          <CardTitle className="line-clamp-2">
            {storeData?.name ?? 'Cửa hàng'}
          </CardTitle>
        </CardHeader>
      </Card>

      {showLocationLoading ? (
        <div className="min-h-screen bg-white">
          <LocationLoadingScreen
            onComplete={() => {
              setShowLocationLoading(false);
            }}
            duration={1500}
            pendingAddress={pendingAddress}
          />
        </div>
      ) : (
        <Card className="overflow-hidden relative border-none rounded-none shadow-none">
          <CardHeader className="p-4 w-full">
            <div className="flex flex-row flex-nowrap justify-start items-center truncate gap-2 w-full">
              <Path className="size-5 flex-none" />
              <CardTitle>Bạn ơi giao món đến đâu?</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-1">
            <div className="relative">
              <Form {...form}>
                <form
                  className="w-full"
                  onChange={handleFormChange}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleConfirmLocation();
                  }}
                >
                  <FormField
                    control={form.control}
                    name="keyword"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="relative flex items-center">
                            <Input
                              placeholder="Nhập địa chỉ giao hàng của bạn"
                              className="h-12 w-full truncate rounded-xl border-neutral-20 bg-neutral-10/30 pl-4 pr-10 text-sm font-bold hover:bg-neutral-30/30 max-h-[115svh]"
                              {...field}
                              onChange={handleInputChange}
                            />
                            <div className="absolute right-3 flex items-center justify-center h-full pointer-events-none">
                              {totalLoading ? (
                                <Loader2 className="size-5 animate-spin text-muted-foreground" />
                              ) : keywordValue && !totalLoading ? (
                                <Button
                                  size="sm"
                                  className="size-6 p-0 pointer-events-auto rounded-full bg-neutral-40 hover:bg-neutral-50"
                                  onClick={handleClearInput}
                                  type="button"
                                >
                                  <X className="size-5" />
                                </Button>
                              ) : (
                                <Search className="size-5" />
                              )}
                            </div>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </form>
              </Form>
              <CardFooter className="py-4 px-0 flex-col gap-3">
                <div className="relative w-full my-2.5">
                  <Separator className="w-full" />
                  <Badge
                    variant="secondary"
                    className="bg-white text-neutral-40 absolute -top-2 left-1/2 -translate-x-1/2 z-[1]"
                  >
                    Hoặc
                  </Badge>
                </div>
                <Button
                  variant="link"
                  size="lg"
                  className="w-full bg-primary-10 [&_svg]:size-6 gap-3 hover:no-underline"
                  onClick={handleUseCurrentLocation}
                  disabled={loadingLocation}
                  type="button"
                >
                  <CurrentLocation className="flex-none [&_rect]:fill-white [&_path]:fill-primary-50" />
                  {loadingLocation
                    ? 'Đang tìm vị trí...'
                    : 'Sử dụng vị trí hiện tại'}
                </Button>
                {selectedCurrentLocation && (
                  <Button
                    type="button"
                    variant="default"
                    size="lg"
                    className="w-full font-bold"
                    onClick={handleConfirmLocation}
                  >
                    Xác nhận địa chỉ
                  </Button>
                )}
              </CardFooter>

              {totalLoading && (
                <div className="mt-4">
                  {[...Array(3)].map((_, index) => (
                    <Fragment key={index}>
                      {index > 0 && (
                        <Separator className="ml-14 w-[calc(100%-3.5rem)]" />
                      )}
                      <AddressSuggestionSkeleton />
                    </Fragment>
                  ))}
                </div>
              )}

              {shouldShowSuggestions && (
                <div className="mt-4">
                  {addressOptions?.map((suggestion, index) => (
                    <Fragment
                      key={`${suggestion.place_id ?? 'no-place-id'}-${index}-${suggestion.address}`}
                    >
                      {index > 0 && (
                        <Separator className="ml-14 w-[calc(100%-3.5rem)]" />
                      )}
                      <AddressSuggestion
                        suggestion={suggestion}
                        onSelect={handleAddressSelect}
                      />
                    </Fragment>
                  ))}
                </div>
              )}

              {shouldShowEmptyResults ? (
                <div className="mt-4 mx-[-16px]">
                  <Card className="flex flex-col px-4 py-6 items-center border-none rounded-none shadow-none w-full">
                    <EmptySearch className="size-20 mb-4" />
                    <CardTitle className="text-center">
                      Không tìm thấy địa chỉ
                    </CardTitle>
                    <CardDescription className="text-center mt-2">
                      Không tìm thấy địa chỉ phù hợp với từ khóa tìm kiếm.
                      <br />
                      Vui lòng thử lại với từ khóa khác.
                    </CardDescription>
                  </Card>
                </div>
              ) : null}

              {!shouldShowSuggestions &&
                !shouldShowEmptyResults &&
                !totalLoading && (
                  <div className="flex flex-col">
                    {/* Show saved addresses if available */}
                    {shouldShowSavedAddresses && !showLocationLoading && (
                      <div className="mt-4 rounded-md">
                        <div className="mb-3 px-0">
                          <h4 className="text-sm font-medium text-muted-foreground">
                            Địa chỉ đã lưu
                          </h4>
                        </div>
                        <ScrollArea className="h-[calc(115svh-485px-env(safe-area-inset-bottom))] md:h-[calc(90svh-580px)]">
                          {userLocationsData?.address_histories?.map(
                            (location, index) => (
                              <Fragment
                                key={`${location.place_id}-${location.save_at}`}
                              >
                                {index > 0 && (
                                  <Separator className="ml-14 w-[calc(100%-3.5rem)]" />
                                )}
                                <SavedAddressItem
                                  userLocation={location}
                                  onSelect={handleAddressSelect}
                                />
                              </Fragment>
                            )
                          )}
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </CardContent>
          {addressError && (
            <div className="px-4 pt-2 pb-4 text-sm text-destructive border border-destructive rounded-md bg-red-10 mx-4">
              * {addressError}
            </div>
          )}
        </Card>
      )}
    </ScrollArea>
  );
}
